import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BG_URL =
  "https://images.unsplash.com/photo-1549946463-0c1ba6fb541f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzaGlweWFyZCUyMG5pZ2h0fGVufDB8fHx8MTc3OTY3OTE5OXww&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    api
      .get(`/auth/reset-password/verify/${token}`)
      .then(({ data }) => {
        setEmail(data.email);
        setStatus("valid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#F6F7F8] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/70" aria-hidden />
      <div className="absolute inset-0 sos-grid-bg opacity-30" aria-hidden />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-8 flex flex-col items-center text-center gap-4">
          <Logo className="h-16 w-auto" showWordmark={false} />
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
              Southern <span className="text-[#0088CC]">Ocean</span>
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.35em] text-[#5B6B7C]">
              Solutions
            </p>
          </div>
        </div>

        {status === "checking" && (
          <div className="sos-surface rounded-lg p-6 text-center text-sm text-[#5B6B7C]" data-testid="reset-checking">
            Verifying reset link…
          </div>
        )}

        {status === "invalid" && (
          <div className="sos-surface rounded-lg p-6 space-y-4 text-center" data-testid="reset-invalid">
            <h2 className="text-lg font-semibold text-[#011836]">Link expired or invalid</h2>
            <p className="text-sm text-[#5B6B7C]">
              This password reset link is no longer valid. Reset links expire after
              1 hour and can only be used once.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block text-sm text-[#0088CC] hover:underline font-medium"
              data-testid="reset-request-new-link"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {status === "valid" && (
          <form
            onSubmit={onSubmit}
            className="sos-surface rounded-lg p-6 space-y-5"
            data-testid="reset-password-form"
          >
            <div>
              <h2 className="text-lg font-semibold text-[#011836]">Choose a new password</h2>
              <p className="mt-1 text-sm text-[#5B6B7C]">
                Resetting the password for <span className="text-[#011836]">{email}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                New password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="reset-password-input"
              />
              <p className="text-[11px] text-[#5B6B7C]">At least 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="reset-confirm-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded"
                data-testid="reset-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide"
              data-testid="reset-submit-btn"
            >
              {submitting ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
